import React from "react";
import { Link } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";

const BagIcon = ({ className = "w-4 h-4" }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 8.25h10.5l.75 12H6l.75-12Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V6a3 3 0 0 1 6 0v3" />
  </svg>
);

const CompareIcon = ({ className = "w-4 h-4" }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M5 6h14M5 6l-3 6h6L5 6Zm14 0-3 6h6l-3-6Z" />
  </svg>
);

const PhoneIcon = ({ className = "w-4 h-4" }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 5.25c0 9.113 7.387 16.5 16.5 16.5h1.5a1.5 1.5 0 0 0 1.5-1.5v-.916c0-.344-.234-.643-.568-.727l-4.423-1.106a1.5 1.5 0 0 0-1.526.47l-.97 1.293a12.04 12.04 0 0 1-9.527-9.527l1.293-.97a1.5 1.5 0 0 0 .47-1.526L5.393 2.818a.75.75 0 0 0-.727-.568H3.75a1.5 1.5 0 0 0-1.5 1.5v1.5Z" />
  </svg>
);

export default function ProductCard({ product, type = "copier", compact = false }) {
  const { addToCart, addToCompare, removeFromCompare, isInCompare } = useShop();
  const { user } = useAuth();
  const { t } = useI18n();

  const productType = type === "copier" ? "copier" : "toner";
  const productId = product.id;
  const compared = isInCompare(productType, productId);

  const handleCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    addToCart(productType, productId);
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (compared) removeFromCompare(productType, productId);
    else addToCompare({ ...product, _product_type: productType, _product_id: productId });
  };

  if (type === "toner") {
    return (
      <div className={`bg-white border rounded-xl ${compact ? "p-2" : "p-4"} hover:shadow-md transition relative group`}>
        {/* Quick action buttons */}
        {!compact && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition z-10">
            <button onClick={handleCart} className="w-7 h-7 rounded-full bg-white text-gray-400 hover:text-blue-500 flex items-center justify-center text-sm shadow-sm" title={t.card_cart}>
              <BagIcon />
            </button>
          </div>
        )}
        <div className={compact ? "flex items-center gap-2" : ""}>
          <div className="flex-1">
            <p className={`font-semibold ${compact ? "text-xs" : "text-sm"} text-gray-800`}>
              {product.brand_name} {product.model_name}
            </p>
            <div className={`flex flex-wrap gap-1 mt-1 ${compact ? "text-[10px]" : "text-xs"}`}>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                product.color === "black" ? "bg-gray-800 text-white" :
                product.color === "cyan" ? "bg-cyan-100 text-cyan-700" :
                product.color === "magenta" ? "bg-pink-100 text-pink-700" :
                product.color === "yellow" ? "bg-yellow-100 text-yellow-700" :
                "bg-purple-100 text-purple-700"
              }`}>
                {product.color === "set" ? (
                  <><span className="w-2.5 h-2.5 rounded-full inline-block bg-cyan-400" /><span className="w-2.5 h-2.5 rounded-full inline-block bg-pink-400" /><span className="w-2.5 h-2.5 rounded-full inline-block bg-yellow-400" /><span className="w-2.5 h-2.5 rounded-full inline-block bg-white border border-gray-300" /></>
                ) : (
                  <span className={`w-2.5 h-2.5 rounded-full inline-block ${
                    product.color === "black" ? "bg-white" :
                    product.color === "cyan" ? "bg-cyan-500" :
                    product.color === "magenta" ? "bg-pink-500" :
                    product.color === "yellow" ? "bg-yellow-400" : "bg-gray-400"
                  }`} />
                )}
                {product.color === "black" ? t.card_black :
                 product.color === "cyan" ? t.card_cyan :
                 product.color === "magenta" ? t.card_magenta :
                 product.color === "yellow" ? t.card_yellow : t.card_set}
              </span>
              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                {product.toner_type === "genuine" ? t.card_genuine :
                 product.toner_type === "compatible" ? t.card_compatible : t.card_remanufactured}
              </span>
              {product.yield_pages && (
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  {t.card_yield} {product.yield_pages.toLocaleString()}{t.card_pages}
                </span>
              )}
            </div>
            {!compact && (
              <div className="mt-2 flex items-center gap-3 text-sm">
                {product.price && (
                  <span className="font-bold text-blue-600">
                    {t.card_genuine} {product.price.toLocaleString()}{t.card_won}
                  </span>
                )}
                {product.price_compatible && (
                  <span className="text-green-600 font-semibold">
                    {t.card_compatible} {product.price_compatible.toLocaleString()}{t.card_won}
                  </span>
                )}
              </div>
            )}
          </div>
          {product.mall_url && (
            <a
              href={product.mall_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5 mt-2"} bg-brand-orange text-white rounded-lg hover:bg-orange-600 transition`}
            >
              <BagIcon /> {t.card_purchase}
            </a>
          )}
        </div>
      </div>
    );
  }

  // Copier card
  return (
    <div className={`bg-white border rounded-xl ${compact ? "p-2" : "p-4"} hover:shadow-md transition relative group`}>
      {/* Quick action buttons */}
      {!compact && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition z-10">
          <button onClick={handleCompare} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shadow-sm ${compared ? "bg-blue-50 text-blue-500" : "bg-white text-gray-400 hover:text-blue-400"}`} title={t.card_compare}>
            <CompareIcon />
          </button>
          <button onClick={handleCart} className="w-7 h-7 rounded-full bg-white text-gray-400 hover:text-blue-500 flex items-center justify-center text-sm shadow-sm" title={t.card_cart}>
            <BagIcon />
          </button>
        </div>
      )}
      <div className={compact ? "flex items-center gap-2" : ""}>
        <div className="flex-1">
          <p className={`font-bold ${compact ? "text-xs" : "text-base"} text-gray-900`}>
            {product.brand_name} {product.model_name}
          </p>
          {!compact && product.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
          )}
          <div className={`flex flex-wrap gap-1 mt-1 ${compact ? "text-[10px]" : "text-xs"}`}>
            <span className={`px-1.5 py-0.5 rounded ${
              product.color_type === "color" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
            }`}>
              {product.color_type === "color" ? t.card_color : t.card_mono}
            </span>
            {product.speed_ppm && (
              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                {product.speed_ppm}ppm
              </span>
            )}
            {product.category_name && (
              <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700">
                {product.category_name}
              </span>
            )}
            {product.auto_tags && product.auto_tags.map((tag, i) => (
              <span key={i} className={`px-1.5 py-0.5 rounded font-medium ${tag.color}`}>
                {tag.label}
              </span>
            ))}
          </div>
          {!compact && product.price_range && (
            <p className="mt-2 text-sm font-semibold text-blue-600">{product.price_range}</p>
          )}
        </div>

        <div className={`flex ${compact ? "flex-col gap-1" : "gap-2 mt-3"}`}>
          <Link
            to={`/copier/${product.id}`}
            className={`text-center ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"} bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition`}
          >
            {t.card_detail}
          </Link>
          {product.mall_url && (
            <a
              href={product.mall_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-1.5 text-center ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"} bg-brand-orange text-white rounded-lg hover:bg-orange-600 transition`}
            >
              <BagIcon /> {t.card_purchase}
            </a>
          )}
          {product.rental_url && (
            <a
              href={product.rental_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-1.5 text-center ${compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"} bg-green-600 text-white rounded-lg hover:bg-green-700 transition`}
            >
              <PhoneIcon /> {t.card_rentalBtn}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
